import { z } from "zod";
const uuid = z.string().uuid();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const boundedText = z.string().trim().min(1).max(160);
const usd = z.string().regex(/^(0|[1-9]\d{0,7})(\.\d{1,6})?$/);
export const productImageJobModeSchema = z.enum(["SEARCH", "GENERATE_ILLUSTRATION"]);
export function validProductImageGtin(value) {
    if (!/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(value))
        return false;
    const digits = [...value].map(Number);
    const check = digits.pop();
    const sum = digits
        .reverse()
        .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === check;
}
export const productImageGtinSchema = z
    .string()
    .regex(/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/)
    .refine(validProductImageGtin, "A valid GTIN with its check digit is required");
/** Syntax fence only; network clients must additionally pin public DNS and peers. */
export const productImagePublicUrlSchema = z
    .string()
    .max(2_000)
    .url()
    .superRefine((value, ctx) => {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" ||
        url.username ||
        url.password ||
        (url.port && url.port !== "443") ||
        !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(host) ||
        /(?:^|\.)(?:localhost|local|internal|invalid|test|example)$/.test(host) ||
        [...url.searchParams.keys()].some((key) => /^(?:token|key|api_key|signature|credential|auth|password|session|x-amz-.+|x-goog-.+)$/i.test(key)))
        ctx.addIssue({
            code: "custom",
            message: "A public HTTPS URL without credentials is required",
        });
});
export const productImageOfficialHostSchema = z
    .string()
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/)
    .refine((host) => !/(?:^|\.)(?:localhost|local|internal|invalid|test|example)$/.test(host));
export const productImageIssueSchema = z.enum([
    "MISSING_GTIN",
    "GTIN_CONFLICT",
    "MISSING_PUBLIC_NAME",
    "VARIANT_NOT_ESTABLISHED",
    "SIZE_NOT_ESTABLISHED",
    "PACKAGING_NOT_ESTABLISHED",
    "NO_PUBLIC_CANDIDATE",
    "UNSAFE_URL",
    "HTTP_VERIFICATION_FAILED",
    "RIGHTS_EVIDENCE_REQUIRED",
    "MANUFACTURER_NOT_ESTABLISHED",
    "EXISTING_IMAGE_PRESERVED",
]);
export const productImageTargetSchema = z
    .object({
    jobId: uuid,
    catalogId: uuid,
    revisionId: uuid,
    productId: uuid,
    sourceRowSha256: hash,
    identitySha256: hash,
    mode: productImageJobModeSchema.default("SEARCH"),
    gtin: productImageGtinSchema.nullable(),
    productName: z.string().trim().min(1).max(300),
    brand: boundedText.nullable(),
    variant: boundedText.nullable(),
    size: boundedText.nullable(),
    packaging: boundedText.nullable(),
    officialHosts: z.array(productImageOfficialHostSchema).max(20),
})
    .strict();
export const productImageGeneratedIllustrationSchema = z
    .object({
    id: uuid,
    identitySha256: hash,
    imageSha256: hash,
    imageBytes: z
        .number()
        .int()
        .min(1)
        .max(4 * 1024 * 1024),
    contentType: z.literal("image/png"),
    model: z.literal("imagen-4.0-fast-generate-001"),
    createdAt: z.string().datetime(),
})
    .strict();
export const productImageGeneratedIllustrationCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    leaseToken: uuid,
    id: uuid,
    identitySha256: hash,
    imageSha256: hash,
    imageBytes: z
        .number()
        .int()
        .min(1)
        .max(4 * 1024 * 1024),
    contentType: z.literal("image/png"),
    model: z.literal("imagen-4.0-fast-generate-001"),
    objectGeneration: z.string().regex(/^[1-9]\d*$/),
})
    .strict();
export const productImageGeneratedIllustrationResultSchema = z
    .object({
    identitySha256: hash,
    illustration: productImageGeneratedIllustrationSchema,
    existingImagePreserved: z.literal(true),
})
    .strict();
const match = z.enum(["EXACT", "MISMATCH", "NOT_ESTABLISHED"]);
export const productImageCandidateInputSchema = z
    .object({
    imageUrl: productImagePublicUrlSchema,
    sourcePageUrl: productImagePublicUrlSchema,
    sourceKind: z.enum(["OFFICIAL_MANUFACTURER", "OTHER_PUBLIC"]),
    match: z.object({ gtin: match, variant: match, size: match, packaging: match }).strict(),
    confidence: z.enum(["EXACT", "PARTIAL", "UNCERTAIN"]),
    rights: z
        .object({
        status: z.enum(["UNKNOWN", "SOURCE_LICENSE_CLAIM"]),
        evidenceUrl: productImagePublicUrlSchema.nullable(),
        statement: z.string().max(1_000).nullable(),
    })
        .strict(),
    evidence: z
        .object({ quote: z.string().max(1_000), publicIdentity: z.string().max(500) })
        .strict(),
    verification: z
        .object({
        status: z.enum(["PUBLIC_HTTP_VERIFIED", "NOT_VERIFIED"]),
        imageSha256: hash.optional(),
        imageBytes: z
            .number()
            .int()
            .min(1)
            .max(10 * 1024 * 1024)
            .optional(),
        contentType: z.enum(["image/jpeg", "image/png", "image/webp", "image/gif"]).optional(),
        sourcePageSha256: hash.optional(),
        retrievedAt: z.string().datetime().optional(),
    })
        .strict()
        .superRefine((value, ctx) => {
        if (value.status === "PUBLIC_HTTP_VERIFIED" &&
            (!value.imageSha256 ||
                !value.imageBytes ||
                !value.contentType ||
                !value.sourcePageSha256 ||
                !value.retrievedAt))
            ctx.addIssue({
                code: "custom",
                message: "Verified candidates require image and source-page receipts",
            });
    }),
})
    .strict();
export const productImageResultSchema = z
    .object({
    identitySha256: hash,
    status: z.enum(["CANDIDATES", "PENDING"]),
    candidates: z.array(productImageCandidateInputSchema).max(5),
    issues: z.array(productImageIssueSchema).max(20),
    existingImagePreserved: z.literal(true),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.status === "CANDIDATES") !== value.candidates.length > 0)
        ctx.addIssue({
            code: "custom",
            message: "Candidate status must agree with the candidate count",
        });
});
export const productImageCheckpointCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    leaseToken: uuid,
    result: productImageResultSchema,
})
    .strict();
export const productImageCheckpointReceiptSchema = z
    .object({
    checkpointId: uuid,
    unitId: uuid,
    identitySha256: hash,
    resultSha256: hash,
    createdAt: z.string().datetime(),
    replayed: z.boolean(),
})
    .strict();
export const productImageJobCreateSchema = z
    .object({
    mode: productImageJobModeSchema.default("SEARCH"),
    revisionId: uuid,
    productIds: z
        .array(uuid)
        .min(1)
        .max(500)
        .refine((ids) => new Set(ids).size === ids.length)
        .optional(),
    budgetUsd: usd.refine((amount) => Number(amount) > 0),
    maxGroundedCalls: z.number().int().min(1).max(100_000).optional(),
    maxImages: z.number().int().min(1).max(50_000).optional(),
    officialHosts: z.array(productImageOfficialHostSchema).max(20).default([]),
    publicIdentityReview: z
        .object({
        reviewId: z.string().min(1).max(160),
        reason: z.string().trim().min(10).max(1_000),
    })
        .strict()
        .optional(),
    identityReviews: z
        .array(z.object({ productId: uuid, reviewId: uuid }).strict())
        .max(500)
        .refine((refs) => new Set(refs.map((r) => r.productId)).size === refs.length)
        .optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.mode === "GENERATE_ILLUSTRATION") {
        if (value.maxImages === undefined || value.maxGroundedCalls !== undefined) {
            context.addIssue({
                code: "custom",
                path: ["maxImages"],
                message: "Illustration jobs require an image count and do not reserve search calls",
            });
        }
        if (value.publicIdentityReview !== undefined || (value.identityReviews?.length ?? 0) > 0) {
            context.addIssue({
                code: "custom",
                path: ["publicIdentityReview"],
                message: "Generated illustrations do not require per-product identity reviews",
            });
        }
    }
    else if (value.maxGroundedCalls === undefined || !value.publicIdentityReview) {
        context.addIssue({
            code: "custom",
            path: ["publicIdentityReview"],
            message: "Search jobs require the reviewed public identity and grounded-call limit",
        });
    }
});
export const productImageJobResumeSchema = z
    .object({ budgetCampaignId: uuid, reason: z.string().trim().min(10).max(1_000) })
    .strict();
export const productImageJobSchema = z
    .object({
    id: uuid,
    catalogId: uuid,
    revisionId: uuid,
    sweepId: uuid,
    mode: productImageJobModeSchema.default("SEARCH"),
    status: z.enum(["BLOCKED", "PENDING", "IN_PROGRESS", "COMPLETED", "PARTIAL"]),
    activation: z
        .object({
        enabled: z.boolean(),
        budgetCampaignId: uuid.nullable(),
        reasons: z
            .array(z.enum([
            "FEATURE_DISABLED",
            "ARGON_COST_BOUND_UNAVAILABLE",
            "IMAGE_MODEL_PRICE_UNQUALIFIED",
            "BUDGET_CAMPAIGN_REQUIRED",
            "CAMPAIGN_UNAVAILABLE",
            "SWEEP_PAUSED",
            "BUDGET_EXHAUSTED",
            "IDENTITY_REVIEW_REQUIRED",
            "PRODUCT_NAME_REQUIRED",
        ]))
            .max(10),
    })
        .strict(),
    counts: z
        .object({
        total: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
        inProgress: z.number().int().nonnegative(),
        done: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        blockedIdentity: z.number().int().nonnegative(),
        candidates: z.number().int().nonnegative(),
        illustrations: z.number().int().nonnegative().default(0),
    })
        .strict(),
    budgetUsd: usd,
    maxGroundedCalls: z.number().int().nonnegative(),
    maxImages: z.number().int().nonnegative().default(0),
    createdAt: z.string().datetime(),
    existingImagePreserved: z.literal(true),
    publication: z.literal("NOT_PERFORMED"),
})
    .strict();
export const productImageIdentityReviewCreateSchema = z
    .object({
    revisionId: uuid,
    expectedSourceRowSha256: hash,
    gtin: productImageGtinSchema,
    variant: boundedText,
    size: boundedText,
    packaging: boundedText,
    sourceUrl: productImagePublicUrlSchema,
    reason: z.string().trim().min(10).max(1_000),
})
    .strict();
export const productImageIdentityReviewSchema = productImageIdentityReviewCreateSchema
    .extend({
    id: uuid,
    productId: uuid,
    catalogId: uuid,
    originalGtin: z.string().nullable(),
    reviewerId: uuid,
    reviewedAt: z.string().datetime(),
})
    .strict();
export const productImageJobsSchema = z
    .object({ items: z.array(productImageJobSchema).max(100) })
    .strict();
export const productImageReviewCreateSchema = z
    .object({
    decision: z.enum(["ACCEPT_CANDIDATE", "REJECT_CANDIDATE"]),
    reason: z.string().trim().min(10).max(1_000),
    rightsEvidence: z
        .object({
        sourceUrl: productImagePublicUrlSchema,
        license: z.string().trim().min(3).max(500),
        scopeOfUse: z.string().trim().min(10).max(1_000),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.decision === "ACCEPT_CANDIDATE" && !value.rightsEvidence)
        ctx.addIssue({
            code: "custom",
            path: ["rightsEvidence"],
            message: "A separate human rights decision is required",
        });
});
export const productImageReviewSchema = z
    .object({
    id: uuid,
    candidateId: uuid,
    decision: z.enum(["ACCEPT_CANDIDATE", "REJECT_CANDIDATE"]),
    reason: z.string(),
    rightsEvidence: productImageReviewCreateSchema.shape.rightsEvidence.nullable(),
    reviewerId: uuid,
    reviewedAt: z.string().datetime(),
    publication: z.literal("NOT_PERFORMED"),
})
    .strict();
export const productImageCandidateSchema = z
    .object({
    id: uuid,
    jobId: uuid,
    unitId: uuid,
    productId: uuid,
    revisionId: uuid,
    identitySha256: hash,
    candidate: productImageCandidateInputSchema,
    createdAt: z.string().datetime(),
    review: productImageReviewSchema.nullable(),
    status: z.enum(["PENDING_REVIEW", "REVIEWED_ACCEPTED_CANDIDATE", "REVIEWED_REJECTED"]),
})
    .strict();
export const productImageJobTargetSchema = z
    .object({
    productId: uuid,
    unitId: uuid.nullable(),
    identitySha256: hash,
    status: z.enum(["PENDING", "IN_PROGRESS", "DONE", "FAILED", "BLOCKED_IDENTITY"]),
    issues: z.array(productImageIssueSchema).max(20),
    checkpointAvailable: z.boolean(),
    error: z
        .object({ code: z.string(), message: z.string(), retryable: z.boolean() })
        .strict()
        .nullable(),
    identityReview: productImageIdentityReviewSchema.nullable(),
})
    .strict();
export const productImageOverlaySchema = z
    .object({
    catalogId: uuid,
    revisionId: uuid,
    productId: uuid,
    issues: z.array(productImageIssueSchema).max(20),
    candidates: z.array(productImageCandidateSchema).max(100),
    generatedIllustration: productImageGeneratedIllustrationSchema.nullable().default(null),
    existingImagePreserved: z.literal(true),
    publication: z.literal("NOT_PERFORMED"),
    reviewedAcceptedCandidateId: uuid.nullable(),
    latestJob: productImageJobSchema.nullable(),
    target: productImageJobTargetSchema.nullable(),
    identityReviews: z.array(productImageIdentityReviewSchema).max(100),
})
    .strict();
export const productImageJobPageSchema = z
    .object({
    job: productImageJobSchema,
    items: z.array(productImageJobTargetSchema).max(100),
    nextCursor: uuid.nullable(),
})
    .strict();
//# sourceMappingURL=product-image-enrichment.js.map